import React, { useState, useEffect, useRef } from 'react';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { convertDateOnlyToUTC, convertUTCToDateOnly } from '../../utils/dateFormat';

interface CustomDatePickerProps {
  value?: string;
  onChange?: (value: string) => void;
  onValidationChange?: (isValid: boolean) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  minDate?: string;
  maxDate?: string;
  showAge?: boolean;
}

export const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value = '',
  onChange,
  onValidationChange,
  label = 'Date',
  placeholder = 'mm/dd/yyyy',
  required = false,
  disabled = false,
  error,
  className = '',
  minDate,
  maxDate,
  showAge = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [displayValue, setDisplayValue] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [isTouched, setIsTouched] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [calculatedAge, setCalculatedAge] = useState<number | null>(null);
  
  // Calendar state
  const [viewDate, setViewDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  
  const containerRef = useRef<HTMLDivElement>(null);

  // Parse UTC date string to local Date object (preserving the exact date)
  const parseUTCDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    try {
      // Convert UTC to date-only format (YYYY-MM-DD) then parse as local date
      const dateOnly = convertUTCToDateOnly(dateString);
      if (!dateOnly) return null;
      
      // Parse the YYYY-MM-DD string as a local date (no timezone interpretation)
      const [year, month, day] = dateOnly.split('-').map(Number);
      return new Date(year, month - 1, day); // month is 0-based
    } catch {
      return null;
    }
  };

  // Format date to mm/dd/yyyy display
  const formatDisplayDate = (date: Date | null): string => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Convert date to UTC for storage (preserving the date)
  const convertToUTC = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateOnlyString = `${year}-${month}-${day}`;
    return convertDateOnlyToUTC(dateOnlyString);
  };

  // Calculate age from date of birth
  const calculateAge = (birthDate: Date): number => {
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return Math.max(0, age);
  };

  // Validate date
  const validateDate = (date: Date | null): { isValid: boolean; message: string } => {
    if (!date) {
      return { isValid: !required, message: required ? 'Date is required' : '' };
    }

    const today = new Date();
    today.setHours(23, 59, 59, 999);

    // Check if date is in the future (for birth dates)
    if (showAge && date > today) {
      return { isValid: false, message: 'Date of birth cannot be in the future' };
    }

    // Check minimum date by calendar day: a minDate with a time later today must not reject today.
    // A bare yyyy-MM-dd (e.g. the 1900-01-01 birth date floor) is that local day; new Date would
    // read it as UTC midnight, the day before west of UTC.
    if (minDate) {
      const bareDay = /^(\d{4})-(\d{2})-(\d{2})$/.exec(minDate);
      const minDateTime = bareDay
        ? new Date(Number(bareDay[1]), Number(bareDay[2]) - 1, Number(bareDay[3]))
        : new Date(minDate);
      const minDay = new Date(minDateTime.getFullYear(), minDateTime.getMonth(), minDateTime.getDate());
      const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      if (day < minDay) {
        return {
          isValid: false,
          message: `Date cannot be before ${formatDisplayDate(minDateTime)}`
        };
      }
    }

    // Check maximum date
    if (maxDate) {
      const maxDateTime = new Date(maxDate);
      if (date > maxDateTime) {
        return { 
          isValid: false, 
          message: `Date must be before ${formatDisplayDate(maxDateTime)}` 
        };
      }
    }

    // Age-specific validations for birth dates
    if (showAge) {
      const age = calculateAge(date);
      if (age > 150) {
        return { isValid: false, message: 'Please verify this date of birth' };
      }
    }

    return { isValid: true, message: '' };
  };

  // Initialize from value prop
  useEffect(() => {
    const date = parseUTCDate(value);
    setSelectedDate(date);
    setDisplayValue(formatDisplayDate(date));
    
    if (date) {
      setViewDate(new Date(date.getFullYear(), date.getMonth(), 1));
      if (showAge) {
        setCalculatedAge(calculateAge(date));
      }
    }
  }, [value, showAge]);

  // Validate when date changes
  useEffect(() => {
    if (isTouched || selectedDate) {
      const validation = validateDate(selectedDate);
      setIsValid(validation.isValid);
      setValidationMessage(validation.message);
      onValidationChange?.(validation.isValid);
    }
  }, [selectedDate, isTouched, required, minDate, maxDate, showAge]);

  // Handle date selection
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setDisplayValue(formatDisplayDate(date));
    setIsTouched(true);
    setIsOpen(false);
    
    const utcString = convertToUTC(date);
    onChange?.(utcString);
    
    if (showAge) {
      setCalculatedAge(calculateAge(date));
    }
  };

  // Handle manual input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setDisplayValue(inputValue);
    setIsTouched(true);

    // Try to parse mm/dd/yyyy format
    const dateMatch = inputValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (dateMatch) {
      const [, month, day, year] = dateMatch;
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      
      if (!isNaN(date.getTime()) && 
          date.getMonth() === parseInt(month) - 1 && 
          date.getDate() === parseInt(day)) {
        setSelectedDate(date);
        setViewDate(new Date(date.getFullYear(), date.getMonth(), 1));
        onChange?.(convertToUTC(date));
        
        if (showAge) {
          setCalculatedAge(calculateAge(date));
        }
      } else {
        setSelectedDate(null);
        onChange?.('');
      }
    } else if (inputValue === '') {
      setSelectedDate(null);
      onChange?.('');
      setCalculatedAge(null);
    }
  };

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const today = new Date();
    
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const isCurrentMonth = date.getMonth() === month;
      const isToday = date.toDateString() === today.toDateString();
      const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
      
      days.push({
        date,
        isCurrentMonth,
        isToday,
        isSelected,
        day: date.getDate()
      });
    }
    
    return days;
  };

  const calendarDays = generateCalendarDays();
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getInputClassName = () => {
    let baseClassName = `
      w-full px-3 py-2 pl-10 border rounded-md
      focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
      disabled:opacity-50 disabled:cursor-not-allowed
      transition-colors duration-200
    `;

    if (error) {
      baseClassName += ' border-red-300 bg-red-50';
    } else if (isTouched && !isValid) {
      baseClassName += ' border-red-300 bg-red-50';
    } else if (isTouched && isValid && selectedDate) {
      baseClassName += ' border-green-300 bg-green-50';
    } else {
      baseClassName += ' border-gray-300';
    }

    return baseClassName;
  };

  return (
    <div className={`space-y-1 ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {/* Calendar Icon */}
        <div className="absolute inset-y-0 left-0 flex items-center pl-3">
          <CalendarIcon className="w-5 h-5 text-gray-400" />
        </div>
        
        <input
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={() => {
            setIsTouched(true);
            setIsOpen(true);
          }}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={getInputClassName()}
        />

        {/* Custom Calendar Dropdown */}
        {isOpen && !disabled && (
          <div className="absolute z-50 mt-1 bg-white border border-gray-300 rounded-md shadow-lg w-80">
            {/* Calendar Header */}
            <div className="flex items-center justify-between p-3 border-b border-gray-200">
              <button
                type="button"
                onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              
              <div className="flex items-center space-x-2">
                <select
                  value={viewDate.getMonth()}
                  onChange={(e) => setViewDate(new Date(viewDate.getFullYear(), parseInt(e.target.value), 1))}
                  className="text-sm border border-gray-300 rounded px-2 py-1"
                >
                  {monthNames.map((month, index) => (
                    <option key={month} value={index}>{month}</option>
                  ))}
                </select>
                
                <select
                  value={viewDate.getFullYear()}
                  onChange={(e) => setViewDate(new Date(parseInt(e.target.value), viewDate.getMonth(), 1))}
                  className="text-sm border border-gray-300 rounded px-2 py-1"
                >
                  {Array.from({ length: 120 }, (_, i) => new Date().getFullYear() - i).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              
              <button
                type="button"
                onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="p-3">
              {/* Day Headers */}
              <div className="grid grid-cols-7 mb-2">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                  <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar Days */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleDateSelect(day.date)}
                    className={`
                      w-8 h-8 text-sm rounded
                      ${day.isCurrentMonth
                        ? day.isSelected
                          ? 'bg-primary-600 text-white'
                          : day.isToday
                            ? 'bg-primary-100 text-primary-600 font-medium'
                            : 'text-gray-900 hover:bg-gray-100'
                        : 'text-gray-400 hover:bg-gray-50'
                      }
                    `}
                  >
                    {day.day}
                  </button>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="mt-3 pt-3 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    const today = new Date();
                    handleDateSelect(today);
                  }}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  Today
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Age Display */}
      {showAge && calculatedAge !== null && isValid && selectedDate && (
        <div className="text-sm text-gray-600">
          Age: {calculatedAge} year{calculatedAge !== 1 ? 's' : ''} old
        </div>
      )}
      
      {/* Error Message */}
      {error && (
        <p className="text-sm text-red-600 flex items-center">
          <ExclamationCircleIcon className="w-4 h-4 mr-1" />
          {error}
        </p>
      )}
      
      {/* Validation Message */}
      {!error && isTouched && validationMessage && (
        <p className={`text-sm flex items-center ${
          isValid ? 'text-blue-600' : 'text-red-600'
        }`}>
          {isValid ? (
            <span className="text-blue-500 mr-1">ℹ️</span>
          ) : (
            <ExclamationCircleIcon className="w-4 h-4 mr-1" />
          )}
          {validationMessage}
        </p>
      )}
      
      {/* Format Help */}
      {!displayValue && !isTouched && (
        <p className="text-xs text-gray-500">
          Use mm/dd/yyyy format or click to open calendar
        </p>
      )}
    </div>
  );
};